import React, { useCallback, useEffect, useState } from 'react'

import { PageTitle } from '../../../../../_metronic/layout/core'
import { KTIcon } from '../../../../../_metronic/helpers'
import {
  SortableTableBody,
  SortableTableRow,
  Table,
  useRowReorder,
} from '../../../../../components/Table'
import { TableHead } from '../../../../../components/Table/TableHead'
import { getErrorCode } from '../../../../common/api/errors'
import { convertUTCtoMSK } from '../../../../../utils'

import { getReasons, reorderReasons } from './core/api'
import { DepositCancelReason } from './core/response.types'
import { DEPOSIT_CANCEL_REASONS_BREADCRUMBS } from './core/consts'
import { ReasonModal } from './components/ReasonModal'
import { RevisionHistory } from './components/RevisionHistory'

const getRowId = (reason: DepositCancelReason) => reason.id

export const DepositCancelReasonsPage: React.FC = () => {
  const [items, setItems] = useState<DepositCancelReason[]>([])
  const [loading, setLoading] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [editing, setEditing] = useState<DepositCancelReason | null>(null)
  const [saving, setSaving] = useState(false)
  const [orderError, setOrderError] = useState<string | null>(null)
  // Every save writes a revision on the server; bumping this reloads the history under the table.
  const [historyKey, setHistoryKey] = useState(0)

  const reorder = useRowReorder(items, getRowId)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const r = await getReasons()
      if (r.data.success) {
        setItems(r.data.data ?? [])
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const openCreate = () => {
    setEditing(null)
    setShowModal(true)
  }

  const openEdit = (reason: DepositCancelReason) => {
    setEditing(reason)
    setShowModal(true)
  }

  const onSaved = async () => {
    await load()
    setHistoryKey((k) => k + 1)
  }

  const startReorder = () => {
    setOrderError(null)
    reorder.open()
  }

  const cancelReorder = () => {
    setOrderError(null)
    reorder.exit()
  }

  const saveOrder = async () => {
    setSaving(true)
    setOrderError(null)
    try {
      const r = await reorderReasons(reorder.ids)
      if (!r.data.success) {
        setOrderError('Не удалось сохранить порядок')
        return
      }
      // Reload before leaving the mode, so the table never flashes the old order in between.
      await onSaved()
      reorder.exit()
    } catch (e: unknown) {
      // The order is written whole: if the dictionary changed meanwhile the backend rejects the
      // request instead of applying half of it; the arrangement stays on screen.
      setOrderError(
        getErrorCode(e) === 'REORDER_SET_MISMATCH'
          ? 'Список изменился — обновите страницу'
          : 'Не удалось сохранить порядок'
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <PageTitle breadcrumbs={DEPOSIT_CANCEL_REASONS_BREADCRUMBS as any}>
        Причины отмены депозита
      </PageTitle>
      <div className='card'>
        <div className='card-header d-flex justify-content-between align-items-center'>
          <h3 className='card-title'>Опросник при отмене депозита на платёжной странице</h3>
          <div className='d-flex gap-2'>
            {reorder.active ? (
              <>
                <button
                  type='button'
                  className='btn btn-sm btn-light'
                  onClick={cancelReorder}
                  disabled={saving}
                >
                  Отмена
                </button>
                <button
                  type='button'
                  className='btn btn-sm btn-success'
                  onClick={saveOrder}
                  disabled={saving || !reorder.dirty}
                >
                  {saving ? 'Сохраняем…' : 'Сохранить'}
                </button>
              </>
            ) : (
              <>
                <button
                  type='button'
                  className='btn btn-sm btn-light'
                  onClick={startReorder}
                  disabled={items.length < 2}
                >
                  Изменить порядок
                </button>
                <button type='button' className='btn btn-sm btn-primary' onClick={openCreate}>
                  Добавить
                </button>
              </>
            )}
          </div>
        </div>
        <div className='card-body'>
          {orderError && <div className='alert alert-danger py-2'>{orderError}</div>}
          {loading ? (
            <div className='text-muted'>Загрузка…</div>
          ) : items.length === 0 ? (
            <div className='text-muted'>Записей нет</div>
          ) : (
            <Table>
              <TableHead>
                <tr>
                  {reorder.active && <th style={{ width: 40 }} />}
                  <th style={{ width: 50 }}>#</th>
                  <th>Ключ</th>
                  <th>Подпись</th>
                  <th>Показывается</th>
                  <th>Создано</th>
                  <th />
                </tr>
              </TableHead>
              <SortableTableBody ids={reorder.ids} disabled={!reorder.active} onMove={reorder.move}>
                {reorder.rows.map((r, index) => (
                  <SortableTableRow key={r.id} id={r.id} disabled={!reorder.active}>
                    {({ attributes, listeners }) => (
                      <>
                        {reorder.active && (
                          <td style={{ width: 40 }}>
                            <span
                              title='Переместить'
                              className='text-gray-400'
                              style={{ cursor: 'grab' }}
                              {...attributes}
                              {...listeners}
                            >
                              <KTIcon iconName='abstract-14' className='fs-2' />
                            </span>
                          </td>
                        )}
                        <td className='fw-bold text-gray-600'>{index + 1}</td>
                        <td className='text-monospace'>{r.code}</td>
                        <td>{r.title}</td>
                        <td>{r.enabled ? 'Да' : '—'}</td>
                        <td>{convertUTCtoMSK(r.createdAt)}</td>
                        <td className='text-end'>
                          <button
                            type='button'
                            className='btn btn-sm btn-light'
                            onClick={() => openEdit(r)}
                            disabled={reorder.active}
                          >
                            Изменить
                          </button>
                        </td>
                      </>
                    )}
                  </SortableTableRow>
                ))}
              </SortableTableBody>
            </Table>
          )}
        </div>
      </div>
      <RevisionHistory reloadKey={historyKey} />
      <ReasonModal
        show={showModal}
        onClose={() => setShowModal(false)}
        editing={editing}
        onSaved={onSaved}
      />
    </>
  )
}

export default DepositCancelReasonsPage
